//ecd:-2109377140H20261022084009_V3.0
/**
 * Shared runtime for the generated API clients.
 *
 * Generated by ElastiCORE. Do not edit outside the ElastiCORE guidelines.
 */

/** One page of results, as Spring Data serialises it. */
export interface Page<T> {
    content: T[];
    totalElements: number;
    totalPages: number;
    number: number;
    size: number;
    first: boolean;
    last: boolean;
}

let baseUrl = '';

/** Where the API lives. Call once at startup; defaults to same-origin. */
export function setApiBaseUrl(url: string): void {
    baseUrl = url.replace(/\/+$/, '');
}

/** What the server answers a refused request with (RFC 9457). */
export interface ProblemDetail {
    type?: string;
    title?: string;
    status?: number;
    detail?: string;
    instance?: string;
    /** What was refused, for a program to branch on: not-found, invalid, state.Order.status.ship ... */
    code?: string;
    /** "field: message" for each field a 400 found wrong. */
    errors?: string[];
    /** The same, as data: the field, the constraint (NotNull, Size ...) and the message in the request's language. */
    fieldErrors?: { field: string; code?: string; message?: string }[];
    [key: string]: unknown;
}

/** Raised for any response the server did not answer 2xx to. */
export class ApiError extends Error {
    /** The problem detail the server sent, when it sent one. */
    readonly problem?: ProblemDetail;
    /** Field -> message, from a 400's errors: what a form shows beside each field. */
    readonly fieldErrors: Record<string, string> = {};

    constructor(readonly status: number, readonly path: string, message: string) {
        super(message);
        this.name = 'ApiError';
        try {
            const parsed = JSON.parse(message);
            if (parsed && typeof parsed === 'object') {
                this.problem = parsed as ProblemDetail;
                if (Array.isArray(this.problem.fieldErrors)) {
                    for (const e of this.problem.fieldErrors) {
                        if (e && e.field) this.fieldErrors[e.field] = String(e.message ?? '');
                    }
                } else {
                    // A server from before fieldErrors: "field: message" strings.
                    for (const entry of this.problem.errors ?? []) {
                        const text = String(entry);
                        const colon = text.indexOf(': ');
                        if (colon > 0) this.fieldErrors[text.slice(0, colon)] = text.slice(colon + 2);
                    }
                }
            }
        } catch {
            // not JSON: the message is the body as it came
        }
    }

    /** What was refused, as a code: the problem's "code", if the server sent one. */
    get code(): string | undefined {
        return typeof this.problem?.code === 'string' ? this.problem.code : undefined;
    }

    /** What to tell the user: the problem's detail, else its title, else the status. */
    get detail(): string {
        // Concatenated: a template literal would read as a slot of the skeleton.
        return this.problem?.detail ?? this.problem?.title ?? 'HTTP ' + this.status;
    }
}

async function send(method: string, path: string, body?: unknown): Promise<Response> {
    return fetch(baseUrl + path, {
        method,
        headers: { 'Content-Type': 'application/json' },
        body: body === undefined ? undefined : JSON.stringify(body),
    });
}

export async function request<T>(method: string, path: string, body?: unknown): Promise<T> {
    const res = await send(method, path, body);
    if (!res.ok) {
        throw new ApiError(res.status, path, await res.text());
    }
    if (res.status === 204) {
        return undefined as T;
    }
    return (await res.json()) as T;
}

/** The same, where the server answers 404 by having nothing to return. */
export async function requestOrNull<T>(method: string, path: string): Promise<T | null> {
    const res = await send(method, path);
    if (res.status === 404) {
        return null;
    }
    if (!res.ok) {
        throw new ApiError(res.status, path, await res.text());
    }
    return (await res.json()) as T;
}

/** A path segment a caller supplied, which may contain anything. */
export function seg(value: string | number | boolean): string {
    return encodeURIComponent(String(value));
}
